import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Payment } from "../../components/Payment";
import { toast } from "react-hot-toast";
import { supabase } from "../../services/supabase";

export interface Template {
  id: string;
  name: string;
  title: string;
  subtitle: string[];
  type: string;
  is_free: boolean;
  color: string;
  sections: string[];
  prompt: string;
  template_id: string;
  created_at: string;
  updated_at: string;
  price: number;
  acquisition_model: string;
}

const TemplatePurchase = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [template, setTemplate] = useState<Template | null>(null);
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!user) {
      toast.error('로그인이 필요합니다.');
      navigate('/login');
      setIsLoading(false);
      return;
    }

    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        if (!templateId) {
          toast.error('템플릿 ID가 올바르지 않습니다.');
          navigate('/dashboard/business-plan');
          return;
        }

        const { data: templateData, error: templateError } = await supabase
          .from('templates')
          .select('id, name, price, acquisition_model')
          .eq('id', templateId)
          .single();

        if (templateError || !templateData) {
          toast.error('템플릿 정보를 불러오는 데 실패했습니다.');
          console.error("Template fetch error:", templateError);
          navigate('/dashboard/business-plan');
          return;
        }

        if (templateData.acquisition_model === 'FREE' || templateData.price == null || templateData.price <= 0) {
          toast.error('구매할 수 없는 템플릿입니다 (무료 또는 가격 미설정).');
          navigate('/dashboard/business-plan');
          return;
        }

        setTemplate(templateData as Template);

        if (!paymentOrderId) {
          const { data: paymentRecord, error: paymentError } = await supabase
            .from('payments')
            .insert({
              user_id: user.id,
              amount: templateData.price,
              status: 'pending',
              payment_method: 'toss',
              item_type: 'TEMPLATE',
              item_id: templateData.id,
            })
            .select('id')
            .single();

          if (paymentError || !paymentRecord) {
            toast.error('결제 주문 생성에 실패했습니다.');
            console.error("Payment record creation error:", paymentError);
            navigate('/dashboard/business-plan');
            return;
          }
          setPaymentOrderId(paymentRecord.id);
        }

      } catch (error) {
        toast.error('데이터 로딩 중 오류가 발생했습니다.');
        console.error("Fetch initial data error:", error);
        navigate('/dashboard/business-plan');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [templateId, navigate, user, authLoading]);

  if (isLoading || authLoading || !template || !paymentOrderId || !user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-lg">결제 정보를 안전하게 준비 중입니다...</p>
          <p className="text-sm text-gray-500 mt-2">잠시만 기다려 주세요.</p>
        </div>
      </div>
    );
  }

  const getBaseApiUrl = () => {
    const isProduction = process.env.NODE_ENV === 'production';
    return isProduction ? window.location.origin : 'http://localhost:3000';
  };

  const paymentProps = {
    amount: template.price,
    orderId: paymentOrderId,
    orderName: `${template.name} 템플릿 구매`,
    customerKey: user.id,
    customerEmail: user.email,
    successUrl: `${getBaseApiUrl()}/api/payments/toss/success`,
    failUrl: `${getBaseApiUrl()}/api/payments/toss/failure`,
  };

  return (
    <div className="container mx-auto p-4 max-w-md">
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="mb-4">
          <h2 className="text-xl font-semibold">{template.name}</h2>
          <p className="text-gray-700">가격: {template.price.toLocaleString()}원</p>
        </div>
        <Payment {...paymentProps} />
      </div>
    </div>
  );
};

export default TemplatePurchase;